import { useState } from 'react';
import {
  CasRenderArrayAsRows,
  CasRenderDate,
  CasTable,
  CasErrorDisplay,
} from 'src/compass/shared/components';
import CasLoader from 'src/compass/shared/components/CasLoader/CasLoader';
import { useCurrentUserEmployee, CasEmployeeSelect } from 'src/compass/application';
import { DateFormat, formatDate, getDateInMoment } from 'src/compass/shared/util/casMoment';

import ICasRenderCellProps from 'src/compass/shared/components/CasTable/ICasRenderCellProps';
import getCasTableCellValue from 'src/compass/shared/components/CasTable/getCasTableCellValue';
import RenderTouchpointFrequency, { useTouchpointCache } from './RenderTouchpointFrequency';
import { NotExcludedOption_Name, TouchpointFrequencySelect } from './TouchpointFrequencySelect';
import CustomerDialog from './CustomerDialog';
import { RenderRiskManagerName } from './RenderRiskManagerName';

export const TOUCHPOINT_ENTITY = 'Utilities/EntityTouchpoint';

export default function TouchpointsTab() {
  const { updateTouchpointListing } = useTouchpointCache();
  const [dialogRowId, setDialogRowId] = useState<number | null>(null);
  const [reloadTable, setReloadTable] = useState<Date | null>(null);

  const { data, isLoading, isError, error } = useCurrentUserEmployee();

  if (isLoading) return <CasLoader />;
  if (isError) return <CasErrorDisplay message={error.message} />;

  return (
    <>
      <CasTable
        entity={TOUCHPOINT_ENTITY}
        includeExport={false}
        refreshBtn
        update={reloadTable}
        columns={[
          {
            field: 'nextTouchpointDate',
            label: 'Next Touchpoint',
            render: (props: ICasRenderCellProps) =>
              props.row?._isPending ? '(updating)' : CasRenderDate(props),
            sx: (row: Record<string, any>) =>
              row.nextTouchpointDate <= formatDate(getDateInMoment(), DateFormat.YMD)
                ? { backgroundColor: '#fde53e' }
                : {},
          },
          {
            field: 'name',
            render: (props: ICasRenderCellProps) => (
              <RenderRiskManagerName {...props} onClick={setDialogRowId} />
            ),
          },
          {
            field: 'contacts',
            sortable: false,
            render: CasRenderArrayAsRows,
          },
          {
            field: 'touchpointFrequency',
            label: 'Freq',
            render: ({ row, column }: ICasRenderCellProps) => {
              const defaultValue = getCasTableCellValue({ row, column });
              return (
                <RenderTouchpointFrequency
                  defaultValue={defaultValue}
                  id={row.id}
                  onUpdated={(nextTouchpointDate) =>
                    updateTouchpointListing({ id: row.id, nextTouchpointDate})
                  }
                />
              );
            },
          },
          {
            field: 'lastTouchpointDate',
            label: 'Last Touchpoint',
            render: CasRenderDate,
          },
        ]}
        filters={[
          {
            name: 'searchName',
            label: 'Name',
          },
          {
            type: 'component',
            name: 'employeeId',
            label: 'RMC',
            initialValue: data?.employeeId,
            component: CasEmployeeSelect,
            componentProps: { params: { meAndMyEmps: true } },
          },
          {
            type: 'component',
            name: 'frequency',
            initialValue: NotExcludedOption_Name,
            component: TouchpointFrequencySelect,
          },
        ]}
      />
      {dialogRowId && (
        <CustomerDialog
          id={dialogRowId}
          onClose={(dataUpdated?: boolean) => {
            setDialogRowId(null);
            if (dataUpdated) setReloadTable(new Date());
          }}
        />
      )}
    </>
  );
}
